import {
    Card,
    CardComplete,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import { CheckIcon, PlusIcon, UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../../components/accordion/accordion.tsx';
import { Button } from '../../../components/ui/button.tsx';
import Text from '../../../components/ui/text.tsx';
import { formatMonthShortDay } from '../../../lib/date-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { TaskFormDialog } from '../../tasks/components/task-form-dialog.tsx';

type NewTaskDialogProps = {
    description: string;
    customerId: string;
    customerName?: string;
    onSubmit: (task: CrmTaskInputDTO) => void;
};

const NewTaskDialog = DialogStateManager.create<NewTaskDialogProps>(
    ({ description, customerId, customerName, onSubmit }: NewTaskDialogProps) => {
        const { visible, remove } = useDialog();
        const { t } = useLocale('tasks');

        return (
            <TaskFormDialog
                open={visible}
                onClose={remove}
                onSubmit={onSubmit}
                title={t('tasks__form_create_title')}
                description={description}
                hideRelatedDetailsLink
                links={[
                    {
                        modelType: SearchEntity.Customer,
                        modelId: customerId,
                        title: customerName ?? null,
                    },
                ]}
            />
        );
    },
);

const extractNodeText = (node: SerializedLexicalNode): string => {
    if ('text' in node && typeof node.text === 'string') {
        return node.text;
    }
    if ('children' in node && Array.isArray(node.children)) {
        return node.children.map(extractNodeText).join('');
    }
    return '';
};

const deriveTaskText = (descriptionRich: SerializedEditorState | null): { title: string; detail: string } => {
    const paragraphs =
        descriptionRich?.root.children
            .map(extractNodeText)
            .map((line) => line.trim())
            .filter(Boolean) ?? [];

    const [titleLine, ...detailLines] = paragraphs;

    const title = titleLine ?? '';
    const detail = detailLines.join(' · ');

    return { title, detail };
};

export type TaskListItem = {
    key: string;
    descriptionRich: SerializedEditorState | null;
    dueDate: Date | null;
    assigneeName?: string;
    completed: boolean;
    dashedBorder: boolean;
    onToggle: (completed: boolean) => void;
};

type LogInteractionTaskListProps = {
    customerId: string;
    customerName?: string;
    items: TaskListItem[];
    onAddNewTask: (task: CrmTaskInputDTO) => void;
    showQuickActions: boolean;
};

export const LogInteractionTaskList = ({
    customerId,
    customerName,
    items,
    onAddNewTask,
    showQuickActions,
}: LogInteractionTaskListProps): ReactNode => {
    const { t, locale } = useLocale('notes');

    const openCreateTask = (description: string): void => {
        void DialogStateManager.show(NewTaskDialog, {
            description,
            customerId,
            customerName,
            onSubmit: onAddNewTask,
        });
    };

    // Without the quick-action pills, an empty task list has nothing to show.
    if (!showQuickActions && items.length === 0) {
        return null;
    }

    return (
        <Accordion type="single" collapsible defaultValue="tasks">
            <AccordionItem value="tasks">
                <AccordionTrigger className="py-3">
                    <Text size="base" weight="semibold" color="neutral-950">
                        {t('notes__tasks_section_title')}
                    </Text>
                </AccordionTrigger>
                <AccordionContent>
                    {showQuickActions && (
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="subtle"
                                rounded
                                leftIcon={PlusIcon}
                                className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                onClick={() => {
                                    openCreateTask(t('notes__tasks_quick_action_call'));
                                }}
                            >
                                {t('notes__tasks_quick_action_call')}
                            </Button>
                            <Button
                                type="button"
                                variant="subtle"
                                rounded
                                leftIcon={PlusIcon}
                                className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                onClick={() => {
                                    openCreateTask(t('notes__tasks_quick_action_clarify_price'));
                                }}
                            >
                                {t('notes__tasks_quick_action_clarify_price')}
                            </Button>
                            <Button
                                type="button"
                                variant="subtle"
                                rounded
                                leftIcon={PlusIcon}
                                className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                onClick={() => {
                                    openCreateTask('');
                                }}
                            >
                                {t('notes__tasks_quick_action_task')}
                            </Button>
                        </div>
                    )}
                    <div className="flex flex-col gap-2 pt-2">
                        {items.map((item) => {
                            const { title, detail } = deriveTaskText(item.descriptionRich);
                            const dueLabel = formatMonthShortDay(item.dueDate, locale);

                            return (
                                <label key={item.key} className="block cursor-pointer">
                                    <Card className={item.dashedBorder ? 'border-dashed' : undefined}>
                                        <CardFront className="card-completed:no-underline!">
                                            <CardComplete
                                                variant="primary"
                                                checked={item.completed}
                                                onChange={(event) => {
                                                    item.onToggle(event.target.checked);
                                                }}
                                            />
                                            <CardContent>
                                                <CardHeader className="flex-nowrap items-start">
                                                    <CardTitle
                                                        className={`line-clamp-2 min-w-0 flex-1${item.completed ? ' line-through' : ''}`}
                                                    >
                                                        {title}
                                                    </CardTitle>
                                                    <CardSubtitle className="shrink-0 whitespace-nowrap">
                                                        {item.assigneeName && (
                                                            <>
                                                                <UserIcon className="size-4" />{' '}
                                                                {item.assigneeName}
                                                            </>
                                                        )}
                                                        {dueLabel && (
                                                            <>
                                                                {item.assigneeName && ' · '}
                                                                {item.completed && (
                                                                    <CheckIcon className="size-4" />
                                                                )}
                                                                {dueLabel}
                                                            </>
                                                        )}
                                                    </CardSubtitle>
                                                </CardHeader>
                                                {detail && !item.completed && (
                                                    <CardDetail className="line-clamp-3 text-content-weak">
                                                        {detail}
                                                    </CardDetail>
                                                )}
                                            </CardContent>
                                        </CardFront>
                                    </Card>
                                </label>
                            );
                        })}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};
